import React, { useContext, useEffect, useState } from "react";
import { Handle } from "react-flow-renderer";
import { Button, Form, Modal } from "react-bootstrap";
import { NodesContext } from "../provider/IvrContext";
import { toast } from "react-toastify";
import {
    BACKEND_API_BASE_URL,
    VOICEMAIL_PROMPT_CATEGORIES,
    VOICEMAIL_MAX_DURATION,
    MISSED_NODE_OPTIONS,
} from "../../../../../utilities/constant";
import { getApiCall } from "../../../../../service/httpCall";
import {
    API_WEBHOOK_LIST,
    API_SMSWEBHOOK_LIST,
} from "../../../../../service/url";


const MissedNode = ({ data, id }) => {
    const { updateNodeData, onRemove } = useContext(NodesContext);

    const mode = data.mode || "";
    const action = data.action || {};
    const maxDuration = data.max_duration || "";

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [promptList, setPromptList] = useState([]);
    const [webhookList, setWebhookList] = useState([]);
    const [smsWebhookList, setSmsWebhookList] = useState([]);
    const [errors, setErrors] = useState({});

    const [draftMode, setDraftMode] = useState(mode);
    const [draftAction, setDraftAction] = useState(action);
    const [draftCategory, setDraftCategory] = useState(action?.prompt_category || "");
    const [draftDuration, setDraftDuration] = useState(maxDuration);
    const [draftWebhookId, setDraftWebhookId] = useState(action?.webhook_id || "");
    const [draftSmsWebhookId, setDraftSmsWebhookId] = useState(action?.sms_webhook_id || "");

    useEffect(() => {
        const load = async () => {
            try {
                if (data.userId) {
                    const resp = await getApiCall(
                        `${BACKEND_API_BASE_URL}/v2/prompt?prompt_status=1&user_id=${data.userId}`
                    );
                    if (Array.isArray(resp)) {
                        setPromptList(resp.filter(p => VOICEMAIL_PROMPT_CATEGORIES.includes(p.prompt_category)));
                    }
                }
                getApiCall(API_WEBHOOK_LIST).then((r) => setWebhookList(r || [])).catch(console.log);
                getApiCall(API_SMSWEBHOOK_LIST).then((r) => setSmsWebhookList(r || [])).catch(console.log);
            } catch (err) {
                console.error(err);
            }
        };
        load();
    }, [data.userId]);

    const updateNode = (updates) => updateNodeData(id, updates);

    const openModal = () => {
        setDraftMode(mode);
        setDraftAction(action);
        setDraftCategory(action?.prompt_category || "");
        setDraftDuration(maxDuration);
        setDraftWebhookId(action?.webhook_id || "");
        setDraftSmsWebhookId(action?.sms_webhook_id || "");
        setErrors({});
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setErrors({});
        setModalIsOpen(false);
    };

    const handleModeChange = (e) => {
        const m = e.target.value;
        setDraftMode(m);
        setDraftAction({});
        setDraftCategory("");
        setDraftDuration("");
        setDraftWebhookId("");
        setDraftSmsWebhookId("");
    };

    const handleCategoryChange = (e) => {
        const cat = e.target.value;
        setDraftCategory(cat);
        setDraftAction((prev) => ({ ...prev, prompt_category: cat, id: "", audio: "", name: "", duration: 0 }));
    };

    const handleAudioChange = (e) => {
        const selected = promptList.find((a) => a.id.toString() === e.target.value);
        if (!selected) {
            setDraftAction((prev) => ({ ...prev, id: "", audio: "", name: "", duration: 0 }));
            return;
        }
        setDraftAction({
            type: draftMode,
            prompt_category: draftCategory,
            id: selected.id,
            audio: selected.prompt_url,
            duration: selected.prompt_duration || 0,
            name: selected.prompt_name,
        });
    };

    const handleDurationChange = (e) => {
        const val = e.target.value;
        if (/^\d*$/.test(val) && (val === "" || Number(val) <= VOICEMAIL_MAX_DURATION)) {
            setDraftDuration(val);
        }
    };

    const handleWebhookChange = (e) => {
        const wid = e.target.value;
        setDraftWebhookId(wid);
        const selected = webhookList.find((w) => w.id.toString() === wid);
        setDraftAction({
            type: "webhook",
            webhook_id: selected?.id || "",
            name: selected?.webhook_name || selected?.name || "",
            url: selected?.url || "",
        });
    };

    const handleSmsWebhookChange = (e) => {
        const wid = e.target.value;
        setDraftSmsWebhookId(wid);
        const selected = smsWebhookList.find((w) => w.id.toString() === wid);
        setDraftAction({
            type: "sms_webhook",
            sms_webhook_id: selected?.id || "",
            name: selected?.webhook_name || "",
            url: selected?.url || "",
            payload: selected?.payload || "",
            request_type: selected?.request_type || "",
            sms_text: selected?.sms_text || "",
            header: selected?.header || "",
        });
    };

    const validate = () => {
        const newErrors = {};
        if (!draftMode) newErrors.mode = "Mode is required";

        if (draftMode === "hangup_with_media" || draftMode === "voicemail") {
            if (draftCategory && !draftAction?.id) {
                newErrors.audio = "Audio is required when category is selected";
            }
        }
        if (draftMode === "voicemail") {
            if (!draftDuration || Number(draftDuration) < 1) newErrors.duration = "Duration required";
            else if (Number(draftDuration) > VOICEMAIL_MAX_DURATION) newErrors.duration = `Max ${VOICEMAIL_MAX_DURATION}s`;
        }
        if (draftMode === "webhook" && !draftWebhookId) newErrors.webhook = "Webhook required";
        if (draftMode === "sms_webhook" && !draftSmsWebhookId) newErrors.smsWebhook = "SMS Webhook required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;
        const updates = { mode: draftMode };
        if (draftMode === "hangup") {
            updates.action = {};
            updates.max_duration = null;
        } else if (draftMode === "webhook" || draftMode === "sms_webhook") {
            updates.action = draftAction;
            updates.max_duration = null;
        } else {
            updates.action = draftAction;
            updates.max_duration = draftMode === "voicemail" ? Number(draftDuration) : null;
        }
        updateNode(updates);
        toast.success("Missed action updated.");
        setModalIsOpen(false);
    };

    const filteredAudio = promptList.filter(
        (p) => !draftCategory || p.prompt_category === draftCategory
    );

    const modeLabel = MISSED_NODE_OPTIONS.find((o) => o.value === mode)?.label || "Missed";

    return (
        <>
            <div
                className="ivr-flow-node"
                style={{
                    width: "100px",
                    border: "2px solid #fd7e14",
                    borderRadius: "12px",
                    background: "#fff",
                    position: "relative",
                    display: "flex",
                    padding: "6px",
                    flexDirection: "column",
                    alignItems: "center",
                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
                }}
            >
                <Handle
                    type="target"
                    position="top"
                    id="missed-target"
                    style={{
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#fd7e14",
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                    }}
                />

                <span
                    className="ivr-flow-node-label"
                    style={{
                        fontSize: "10px",
                        fontWeight: "bold",
                        color: "#333",
                        textAlign: "center",
                        textTransform: "capitalize",
                    }}
                >
                    Missed: {modeLabel}
                </span>

                <div style={{ display: "flex", gap: "8px", marginTop: 4 }}>
                    <i
                        className="fa fa-cog ivr-flow-node-icon"
                        style={{ color: "#fd7e14", fontSize: "12px", cursor: "pointer" }}
                        onClick={openModal}
                    />
                    <i
                        className="fa fa-trash ivr-flow-node-icon"
                        style={{ color: "#fd7e14", fontSize: "12px", cursor: "pointer" }}
                        onClick={() => onRemove(id)}
                    />
                </div>
            </div>

            <Modal size="lg" show={modalIsOpen} onHide={closeModal} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Missed Call Action</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>Action<Form.Text className="text-danger">*</Form.Text></Form.Label>
                            <Form.Control as="select" value={draftMode} onChange={handleModeChange}>
                                <option value="">Select Action</option>
                                {MISSED_NODE_OPTIONS.map((m) => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </Form.Control>
                            {errors.mode && <Form.Text className="text-danger">{errors.mode}</Form.Text>}
                        </Form.Group>

                        {(draftMode === "hangup_with_media" || draftMode === "voicemail") && (
                            <>
                                <Form.Group style={{ marginTop: 15 }}>
                                    <Form.Label>Prompt Category</Form.Label>
                                    <Form.Control as="select" value={draftCategory} onChange={handleCategoryChange}>
                                        <option value="">Select Category</option>
                                        {VOICEMAIL_PROMPT_CATEGORIES.map((c) => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </Form.Control>
                                    {errors.category && <Form.Text className="text-danger">{errors.category}</Form.Text>}
                                </Form.Group>

                                <Form.Group style={{ marginTop: 15 }}>
                                    <Form.Label>Select Audio</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={draftAction?.id || ""}
                                        onChange={handleAudioChange}
                                        disabled={!draftCategory}
                                    >
                                        <option value="">Select Audio</option>
                                        {filteredAudio.map((item) => (
                                            <option key={item.id} value={item.id}>{item.prompt_name}</option>
                                        ))}
                                    </Form.Control>
                                    {errors.audio && <Form.Text className="text-danger">{errors.audio}</Form.Text>}
                                </Form.Group>
                            </>
                        )}

                        {draftMode === "voicemail" && (
                            <Form.Group style={{ marginTop: 15 }}>
                                <Form.Label>Max Voicemail Duration (sec, max {VOICEMAIL_MAX_DURATION})<Form.Text className="text-danger">*</Form.Text></Form.Label>
                                <Form.Control
                                    type="number"
                                    min="1"
                                    max={VOICEMAIL_MAX_DURATION}
                                    value={draftDuration}
                                    onChange={handleDurationChange}
                                />
                                {errors.duration && <Form.Text className="text-danger">{errors.duration}</Form.Text>}
                            </Form.Group>
                        )}

                        {draftMode === "webhook" && (
                            <Form.Group style={{ marginTop: 15 }}>
                                <Form.Label>Select Webhook<Form.Text className="text-danger">*</Form.Text></Form.Label>
                                <Form.Control as="select" value={draftWebhookId} onChange={handleWebhookChange}>
                                    <option value="">Select Webhook</option>
                                    {webhookList.map((w) => (
                                        <option key={w.id} value={w.id}>{w.webhook_name || w.name}</option>
                                    ))}
                                </Form.Control>
                                {errors.webhook && <Form.Text className="text-danger">{errors.webhook}</Form.Text>}
                            </Form.Group>
                        )}

                        {draftMode === "sms_webhook" && (
                            <Form.Group style={{ marginTop: 15 }}>
                                <Form.Label>Select SMS Webhook<Form.Text className="text-danger">*</Form.Text></Form.Label>
                                <Form.Control as="select" value={draftSmsWebhookId} onChange={handleSmsWebhookChange}>
                                    <option value="">Select SMS Webhook</option>
                                    {smsWebhookList.map((w) => (
                                        <option key={w.id} value={w.id}>{w.webhook_name}</option>
                                    ))}
                                </Form.Control>
                                {errors.smsWebhook && <Form.Text className="text-danger">{errors.smsWebhook}</Form.Text>}
                            </Form.Group>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={handleSave}>Save</Button>
                    <Button variant="secondary" onClick={closeModal}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default MissedNode;
